import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, MenuItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReplayIcon from '@mui/icons-material/Replay';
import { CreateAccrualCode } from 'components/dialog/accrual_codes/create';
import { UpdateAccrualCode } from 'components/dialog/accrual_codes/update';
import { ReadAccrualCode } from 'components/dialog/accrual_codes/read';
import {
    buildSectionSubpageActionPath,
    buildSectionSubpageBasePath,
    parseSectionSubpageActionRoute,
} from 'utils/sectionSubpageRouting';

interface SubMenuProps {
    selected: any;
    cleanSelected: () => void;
    rows: any;
    contextMenu: any;
    setContextMenu: (value: any) => void;
    reloading: () => void;
}


const SubMenu: FC<SubMenuProps> = ({ selected, cleanSelected, rows, contextMenu, setContextMenu, reloading }) => {
    const location = useLocation();
    const navigate = useNavigate();

    const { t } = useTranslation()
    const [openAdd, setOpenAdd] = useState(false)
    const [openEdit, setOpenEdit] = useState(false)
    const [openShow, setOpenShow] = useState(false)

    const modalRoute = parseSectionSubpageActionRoute(
        location.pathname,
        'accruals',
        'accrual_codes',
        ['add', 'update', 'detail']
    );
    const routeSelected = modalRoute.id
        ? rows.find((row: any) => String(row?.id) === String(modalRoute.id))
        : null;
    const selectedForDialog = routeSelected ? [routeSelected] : selected;

    const effectiveOpenAdd = modalRoute.action === 'add' || openAdd;
    const effectiveOpenEdit = modalRoute.action === 'update' || openEdit;
    const effectiveOpenShow = modalRoute.action === 'detail' || openShow;

    const closeToBase = () => {
        const basePath = buildSectionSubpageBasePath('accruals', 'accrual_codes');
        if (modalRoute.action) {
            navigate(basePath);
            return;
        }

        setOpenAdd(false);
        setOpenEdit(false);
        setOpenShow(false);
    };

    interface Option {
        name: string;
        icon: React.ReactNode;
        click: () => void;
        color: string;
        divider: boolean;
        disabled: boolean;
        selected: boolean;
        onlyOne: boolean;
        hidden: boolean;
    }

    const options: Option[] = [
        {
            name: t('accruals.accrual_codes.context_menu.add'),
            icon: <AddIcon />,
            click: () => {
                navigate(buildSectionSubpageActionPath('accruals', 'accrual_codes', 'add'))
                setContextMenu(null)
            },
            color: '',
            divider: false,
            disabled: false,
            selected: false,
            onlyOne: false,
            hidden: (JSON.parse(localStorage.getItem('permissions') || '[]') as string[]).find((permision: string) => permision === 'timekeeper.CreateHoliday') ? false : true,
        },
        {
            name: t('accruals.accrual_codes.context_menu.info'),
            icon: <VisibilityIcon />,
            click: () => {
                if (!selected?.[0]?.id) return;
                navigate(buildSectionSubpageActionPath('accruals', 'accrual_codes', 'detail', selected[0].id))
                setContextMenu(null)
            },
            color: '',
            divider: false,
            disabled: selected.length <= 0 ? true : false,
            selected: true,
            onlyOne: false,
            hidden: selected.length <= 0 || !(JSON.parse(localStorage.getItem('permissions') || '[]') as string[]).find((permision: string) => permision === 'timekeeper.HolidayById') ? true : false,
        },
        {
            name: t('accruals.accrual_codes.context_menu.edit'),
            icon: <EditIcon />,
            click: () => {
                if (!selected?.[0]?.id) return;
                navigate(buildSectionSubpageActionPath('accruals', 'accrual_codes', 'update', selected[0].id))
                setContextMenu(null)
            },
            color: '',
            divider: false,
            disabled: selected.length === 1 ? false : true,
            selected: false,
            onlyOne: true,
            hidden: selected.length !== 1 || !(JSON.parse(localStorage.getItem('permissions') || '[]') as string[]).find((permision: string) => permision === 'timekeeper.PatchHolidayById') ? true : false,
        },
        {
            name: t('accruals.accrual_codes.context_menu.reload'),
            icon: <ReplayIcon />,
            click: () => {
                reloading()
                setContextMenu(null)
            },
            color: '',
            divider: true,
            disabled: false,
            selected: false,
            onlyOne: false,
            hidden: false,
        }
    ]

    return (
        <>
            <Menu
                open={ Boolean(contextMenu) }
                onClose={() => setContextMenu(null) }
                anchorReference="anchorPosition"
                anchorPosition={ contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined }
            >
                {options.map((option, index) => (

                    <div key={option.name} hidden={option.onlyOne && selected?.length !== 1}>
                        {option.divider && <Divider />}
                        <MenuItem onClick={option.click} disabled={option.disabled} >
                            <ListItemIcon>{option.icon}</ListItemIcon>
                            <ListItemText primary={option.name} slotProps={{ primary: { color: option.color } }} />
                        </MenuItem>
                    </div>
                ))}
            </Menu>
            <CreateAccrualCode open={effectiveOpenAdd} setOpen={(value: boolean) => { if (!value) { closeToBase(); } else { setOpenAdd(true); } }} rows={rows} reloading={reloading} />
            <ReadAccrualCode open={effectiveOpenShow} setOpen={(value: boolean) => { if (!value) { closeToBase(); } else { setOpenShow(true); } }} selectedRecord={selectedForDialog[0]} />
            <UpdateAccrualCode open={effectiveOpenEdit} setOpen={(value: boolean) => { if (!value) { closeToBase(); } else { setOpenEdit(true); } }} rows={rows} rowToEdit={selectedForDialog[0]} reloading={reloading} />
        </>
    )
}

export { SubMenu };